/**
 * What a 3D model may load. `<model-viewer>` loads glTF through three.js, and
 * a glTF names its own buffers and textures by URI: a file an app (or anyone)
 * wrote could point them at an outside host and carry data out in the URL,
 * outside every frame guard and confirm. Every model-viewer load goes through
 * one URL hook (ModelViewerElement.mapURLs), which allows only:
 *
 * - the model file itself, as the viewer resolved it through authorized storage
 *   (registered while that viewer is on screen);
 * - `data:` URIs (embedded buffers and images);
 * - `blob:` URLs (three.js makes these for images packed in a GLB).
 *
 * Anything else becomes an empty data URI, so it is never requested. Viewers
 * never resolve a model's sibling files, so a multi-file glTF whose .bin or
 * textures sit beside it shows without them rather than reaching out.
 */

export const MODEL_VIEWER_BLOCKED_URL = 'data:application/octet-stream;base64,';

const sources = new Map<string, number>();

function absolute(url: string): string {
  try {
    return new URL(url, globalThis.location?.href ?? 'http://localhost/').href;
  } catch {
    return url;
  }
}

/** Allow a model file for as long as its viewer is on screen; returns the release. */
export function allowModelViewerSource(src: string): () => void {
  const key = absolute(src);
  sources.set(key, (sources.get(key) ?? 0) + 1);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const count = (sources.get(key) ?? 1) - 1;
    if (count <= 0) sources.delete(key);
    else sources.set(key, count);
  };
}

export function modelViewerUrlAllowed(url: string): boolean {
  if (/^(?:data|blob):/i.test(url.trim())) return true;
  return sources.has(absolute(url));
}

/** The URL hook for every model-viewer load. */
export function modelViewerUrlModifier(url: string): string {
  return modelViewerUrlAllowed(url) ? url : MODEL_VIEWER_BLOCKED_URL;
}

type UrlMappingElement = { mapURLs: (callback: (url: string) => string) => void };

function hasMapURLs(value: unknown): value is UrlMappingElement {
  return typeof value === 'function' && 'mapURLs' in value && typeof value.mapURLs === 'function';
}

/**
 * Install the hook on the model-viewer element class. Returns false when the
 * class offers no URL hook; the viewer then refuses to show models (fail closed).
 */
export function installModelViewerUrlPolicy(elementClass: unknown): boolean {
  if (!hasMapURLs(elementClass)) return false;
  elementClass.mapURLs(modelViewerUrlModifier);
  return true;
}
